import { Link } from "react-router"

import type { BuyerAnalyticsRow, AnalyticsTotals } from "@/api/hooks/dashboard"
import {
  DASHBOARD_BUYER_COLUMNS,
  DASHBOARD_FIRST_COL_BUYER,
} from "../lib/columns"
import { AnalyticsTableShell } from "./AnalyticsTableShell"

/**
 * Buyer-tab wrapper around `AnalyticsTableShell`. Owns the link-cell
 * factory for the pinned `username` column — it navigates to the
 * still-unported React Ads Manager (T3.7) preserving the current
 * date range + network filter so the hand-off matches legacy
 * behaviour (`dashboard.html:2196-2210`).
 */

interface BuyerAnalyticsTableProps {
  rows: BuyerAnalyticsRow[]
  totals: AnalyticsTotals | null
  columnOrder: string[]
  sortKey: string
  sortDir: "asc" | "desc"
  onSort: (key: string, columnType: "string" | "number") => void
  onColumnOrderChange: (order: string[]) => void
  startDate: string
  endDate: string
  network: string
  isLoading?: boolean
}

function buildAdsManagerLink(
  userId: number,
  startDate: string,
  endDate: string,
  network: string,
): string {
  const params = new URLSearchParams()
  params.set("user_id", String(userId))
  if (startDate && endDate) {
    params.set("start_date", startDate)
    params.set("end_date", endDate)
  }
  if (network && network !== "all") params.set("network", network)
  return `/adsmanager?${params.toString()}`
}

export function BuyerAnalyticsTable({
  rows,
  totals,
  columnOrder,
  sortKey,
  sortDir,
  onSort,
  onColumnOrderChange,
  startDate,
  endDate,
  network,
  isLoading,
}: BuyerAnalyticsTableProps) {
  return (
    <AnalyticsTableShell<BuyerAnalyticsRow>
      tableId="buyerAnalyticsTable"
      columns={DASHBOARD_BUYER_COLUMNS}
      pinnedColumnId={DASHBOARD_FIRST_COL_BUYER}
      rows={rows}
      totals={totals}
      columnOrder={columnOrder}
      sortKey={sortKey}
      sortDir={sortDir}
      onSort={onSort}
      onColumnOrderChange={onColumnOrderChange}
      isLoading={isLoading}
      renderFirstCell={(row) =>
        row.user_id < 0 ? (
          <span className="text-[#e0e0e0]">{row.username}</span>
        ) : (
          <Link
            to={buildAdsManagerLink(row.user_id, startDate, endDate, network)}
            className="text-[#e0e0e0] hover:text-[#8b9aff] hover:underline underline-offset-2"
          >
            {row.username}
          </Link>
        )
      }
      renderFirstTotalsCell={() => (
        <strong className="text-[#e0e0e0]">Totals</strong>
      )}
    />
  )
}
